import { z } from 'zod';
import { ROLES } from '../types/roles.js';
/**
 * Auth-Contracts (shared, 06 – Tech-Stack). Einzige Typquelle für Login/Session
 * in Server und Web. Rollenprüfung passiert serverseitig (08 – Rollen & Rechte).
 */
/** Login: Name + Geheimnis (Passwort bei Erwachsenen, PIN bei Kindern). */
export const loginRequestSchema = z.object({
    name: z.string().min(1, 'Name fehlt'),
    secret: z.string().min(1, 'Passwort/PIN fehlt'),
    deviceLabel: z.string().max(120).optional(),
    /**
     * Token-Auth für native Apps (Capacitor, ADR-042): liefert den Session-Token
     * zusätzlich im Response-Body (statt nur als Cookie) und nutzt eine langlebige
     * Session. Browser lassen das Feld weg → Cookie-Flow unverändert. Alternativ per
     * Header `X-Client: native`.
     */
    tokenAuth: z.boolean().optional(),
});
/** Öffentliches Mitglieder-Objekt — NIE Hashes o. Ä. an den Client. */
export const memberDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string(), // Vorname
    email: z.string(),
    birthdate: z.string(), // YYYY-MM-DD
    role: z.enum(ROLES),
    color: z.string(),
    parentTitle: z.string().nullable(), // „Mama"/„Papa" für generierte Termine (Dienstplan)
    avatarUrl: z.string().nullable(),
    // Kind darf einen eigenen Kalender-Feed (ICS-Export) erzeugen — von Eltern
    // freigeschaltet (ADR-086). Erwachsene dürfen immer; optional = ältere Server.
    calendarFeedAllowed: z.boolean().optional(),
});
/** Antwort auf Login / GET me. */
export const authMeResponseSchema = z.object({
    member: memberDtoSchema,
});
/**
 * Antwort auf Login. Wie AuthMeResponse, plus optionaler Klartext-Token für native
 * Clients (nur gesetzt, wenn tokenAuth/`X-Client: native`). Der Token wird auf dem
 * Gerät sicher gespeichert (Keychain/Keystore) und als `Authorization: Bearer` bzw.
 * für den WebSocket als First-Message-Auth genutzt (ADR-042).
 */
export const loginResponseSchema = authMeResponseSchema.extend({
    token: z.string().optional(),
    expiresAt: z.string().optional(), // ISO-8601
});
/**
 * Status der optionalen Authentik-Anmeldung (OIDC, ADR-082).
 * - `enabled`: Server ist konfiguriert → das Web darf den SSO-Knopf zeigen.
 *   Ist er false, verhält sich alles exakt wie ohne SSO (Break-Glass).
 * - `linked`: die aktuelle Session hat ein Authentik-Konto verknüpft
 *   (ohne Session immer false).
 */
export const oidcStatusSchema = z.object({
    enabled: z.boolean(),
    linked: z.boolean(),
});
// ── Erst-Einrichtung (erster Aufruf, kein Konto vorhanden) ─────────
export const setupStatusSchema = z.object({ needsSetup: z.boolean() });
export const setupRequestSchema = z.object({
    familyName: z.string().min(1).max(80).optional(),
    name: z.string().min(1).max(60),
    email: z.string().email(),
    birthdate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als YYYY-MM-DD'),
    password: z.string().min(6, 'Mindestens 6 Zeichen'),
});
// ── Mitgliederverwaltung (Erwachsene) ─────────────────────────────
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Farbe als #rrggbb');
const birthdateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als YYYY-MM-DD');
// Geheimnis-Mindestlängen (Low): Kinder-PIN ≥ 4 Ziffern, Erwachsenen-Passwort ≥ 6.
// Verhindert triviale 1-stellige PINs; der Seed „1234" bleibt gerade noch gültig.
const MIN_PIN = 4;
const MIN_PASSWORD = 6;
const secretForRole = (role, secret) => role === 'adult' ? secret.length >= MIN_PASSWORD : secret.length >= MIN_PIN;
export const createMemberSchema = z
    .object({
    name: z.string().min(1).max(60),
    email: z.string().email(),
    birthdate: birthdateStr,
    role: z.enum(['adult', 'child']),
    color: hexColor,
    parentTitle: z.string().max(20).nullable().optional(),
    secret: z.string().min(MIN_PIN), // Passwort (adult) bzw. PIN (child)
})
    .refine((d) => secretForRole(d.role, d.secret), {
    message: 'PIN mind. 4 Zeichen, Passwort mind. 6 Zeichen',
    path: ['secret'],
});
export const updateMemberSchema = z
    .object({
    name: z.string().min(1).max(60).optional(),
    email: z.string().email().optional(),
    birthdate: birthdateStr.optional(),
    role: z.enum(['adult', 'child']).optional(),
    color: hexColor.optional(),
    parentTitle: z.string().max(20).nullable().optional(),
    secret: z.string().min(MIN_PIN).optional(),
    calendarFeedAllowed: z.boolean().optional(),
})
    .refine((d) => d.secret === undefined || d.role === undefined || secretForRole(d.role, d.secret), {
    message: 'PIN mind. 4 Zeichen, Passwort mind. 6 Zeichen',
    path: ['secret'],
});
//# sourceMappingURL=auth.js.map